/* ============================================================
   GlobalAssistant — owns the AIPanel + VoiceMode for AppShell
   routes that don't mount the legacy MainApp. Listens to
   dathent:assistant:open / :close events broadcast by AssistantFAB.
   ============================================================ */
import React, { useEffect, useState, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AIPanel, VoiceMode } from '../AIAssistant.jsx';

const OPEN_EVENT = 'dathent:assistant:open';
const CLOSE_EVENT = 'dathent:assistant:close';
const STATE_EVENT = 'dathent:assistant:state';

function screenFromPath(pathname = '') {
  const segments = String(pathname || '').split('/').filter(Boolean);
  if (segments[0] !== 'app') return segments[0] || 'dashboard';
  if (segments.length === 1) return 'dashboard';
  const tail = segments[1];
  if (tail === 'library') return 'content';
  if (tail === 'create') return 'create';
  if (tail === 'calendar') return 'scheduler';
  if (tail === 'plan') return 'brand';
  if (tail === 'settings') return 'settings';
  if (tail === 'batch') return 'create';
  if (tail === 'post') return 'preview';
  if (tail === 'help') return 'help';
  return tail || 'dashboard';
}

const SCREEN_TO_PATH = {
  dashboard: '/app',
  content: '/app/library',
  create: '/app/create',
  scheduler: '/app/calendar',
  brand: '/app/plan',
  settings: '/app/settings',
  preview: '/app/library',
  editor: '/app/library',
  carousel: '/app/library',
  social: '/app/settings?tab=social',
  billing: '/app/settings?tab=billing',
  help: '/app/help',
};

export default function GlobalAssistant() {
  const [chatOpen, setChatOpen] = useState(false);
  const [voiceOpen, setVoiceOpen] = useState(false);
  const [notificationContext, setNotificationContext] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();

  const currentScreen = useMemo(() => screenFromPath(location.pathname), [location.pathname]);

  useEffect(() => {
    const onOpen = (event) => {
      setChatOpen(true);
      if (event?.detail && typeof event.detail === 'object') {
        const context = event.detail.context;
        if (context && typeof context === 'object') {
          setNotificationContext({ ...context, requestId: context.requestId || Date.now() });
        }
      }
      window.dispatchEvent(new CustomEvent(STATE_EVENT, { detail: { open: true } }));
    };
    const onClose = () => {
      setChatOpen(false);
      window.dispatchEvent(new CustomEvent(STATE_EVENT, { detail: { open: false } }));
    };
    window.addEventListener(OPEN_EVENT, onOpen);
    window.addEventListener(CLOSE_EVENT, onClose);
    return () => {
      window.removeEventListener(OPEN_EVENT, onOpen);
      window.removeEventListener(CLOSE_EVENT, onClose);
    };
  }, []);

  const handleNavigate = (target, options = {}) => {
    if (!target) return;
    if (typeof target === 'string' && target.startsWith('/')) {
      navigate(target, options.replace ? { replace: true } : undefined);
      return;
    }
    const basePath = SCREEN_TO_PATH[target] || '/app';
    let nextPath = basePath;
    if (options.batchId) {
      nextPath = `/app/batch/${encodeURIComponent(options.batchId)}`;
    } else if (options.contentId) {
      nextPath = `/app/post/${encodeURIComponent(options.contentId)}`;
    }
    navigate(nextPath, options.replace ? { replace: true } : undefined);
    if (options.keepAssistantOpen) setChatOpen(true);
    else if (!options.batchId && !options.contentId) {
      // For plain navigation, leave the panel as-is; only close on explicit ask.
    }
  };

  const handleClose = () => {
    setChatOpen(false);
    window.dispatchEvent(new CustomEvent(STATE_EVENT, { detail: { open: false } }));
  };

  return (
    <>
      <AIPanel
        open={chatOpen}
        onClose={handleClose}
        currentScreen={currentScreen}
        selectedEntity={null}
        notificationContext={notificationContext}
        onVoice={() => setVoiceOpen(true)}
        navigate={handleNavigate}
      />
      {voiceOpen && <VoiceMode onClose={() => setVoiceOpen(false)} />}
    </>
  );
}
